import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, MessageSquareText, CalendarClock, Receipt, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SatisfactionPanel } from "@/components/Feedback";
import { Star } from "lucide-react";

const fmt = (d) => (d ? new Date(`${d.slice(0, 10)}T00:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "");

const StudyTile = ({ s }) => (
  <Link to={`/studies/${s.id}`} data-testid={`workload-study-${s.id}`}
    className={`block rounded-lg border p-3 bg-slate-950/50 hover:border-emerald-500/50 transition-colors ${s.overdue || s.invoice_overdue ? "border-rose-500/40" : s.open_requests ? "border-amber-500/40" : "border-slate-800"}`}>
    <p className="text-sm font-semibold text-slate-100 truncate">{s.name}</p>
    <p className="text-xs text-slate-500 truncate mb-2">{s.client || "No client"}{s.status !== "ready" ? ` · ${s.status}` : ""}</p>
    <div className="flex flex-wrap gap-1.5 text-[11px]">
      {s.due_date && <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full ${s.overdue ? "bg-rose-500/15 text-rose-300" : "bg-slate-800 text-slate-300"}`}><CalendarClock className="h-3 w-3" />{s.overdue ? "Overdue " : "Due "}{fmt(s.due_date)}</span>}
      {s.open_requests > 0 && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-300"><MessageSquareText className="h-3 w-3" />{s.open_requests} request{s.open_requests > 1 ? "s" : ""} · since {fmt(s.oldest_request)}</span>}
      {s.unpaid_invoices > 0 && <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full ${s.invoice_overdue ? "bg-rose-500/15 text-rose-300" : "bg-cyan-500/10 text-cyan-300"}`}><Receipt className="h-3 w-3" />Unpaid{s.invoice_due ? ` · due ${fmt(s.invoice_due)}` : ""}</span>}
    </div>
  </Link>
);

const Column = ({ c, me, sat }) => (
  <section className={`w-80 shrink-0 rounded-xl border bg-slate-900/70 flex flex-col max-h-[calc(100vh-14rem)] ${c.id === me ? "border-emerald-500/50" : "border-slate-800"}`} data-testid={`workload-col-${c.id}`}>
    <div className="p-4 border-b border-slate-800">
      <div className="flex items-center justify-between gap-2">
        <p className="font-display font-semibold truncate">{c.name}{c.id === me && <span className="ml-2 text-[10px] text-emerald-400 uppercase tracking-wider">You</span>}</p>
        <span className="num text-xs text-slate-400">{c.summary.studies}</span>
      </div>
      <div className="flex gap-3 mt-2 text-[11px] text-slate-400">
        <span data-testid={`workload-requests-${c.id}`}><span className="num text-amber-300">{c.summary.open_requests}</span> open requests</span>
        <span data-testid={`workload-overdue-${c.id}`}><span className="num text-rose-300">{c.summary.overdue}</span> overdue</span>
        {sat && <span className="flex items-center gap-0.5" data-testid={`workload-rating-${c.id}`}><Star className="h-3 w-3 fill-amber-400 text-amber-400" /><span className="num text-amber-300">{sat.average.toFixed(1)}</span> ({sat.count})</span>}
      </div>
    </div>
    <div className="p-3 space-y-2 overflow-y-auto">
      {c.studies.length ? c.studies.map((s) => <StudyTile key={s.id} s={s} />) : <p className="text-xs text-slate-500 p-2">No studies.</p>}
    </div>
  </section>
);

export default function Workload() {
  const { user } = useAuth();
  const [cols, setCols] = useState(null);
  const [err, setErr] = useState(false);
  const [sat, setSat] = useState(null);
  useEffect(() => { api.get("/reports/satisfaction").then((r) => setSat(r.data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/reports/workload").then((r) => setCols(r.data)).catch(() => setErr(true)); }, []);
  if (err) return <div className="min-h-[60vh] grid place-items-center text-slate-400" data-testid="workload-error">Could not load the workload board. Please refresh.</div>;
  if (!cols) return <div className="min-h-[60vh] grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-emerald-400" /></div>;
  const totals = cols.reduce((a, c) => ({ requests: a.requests + c.summary.open_requests, overdue: a.overdue + c.summary.overdue }), { requests: 0, overdue: 0 });
  const unassigned = cols.find((c) => c.id === "unassigned");
  const ordered = [...cols.filter((c) => c.id === user?.id), ...cols.filter((c) => c.id !== user?.id && c.id !== "unassigned"), ...(unassigned?.studies.length ? [unassigned] : [])];

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-12 fade-up" data-testid="workload-page">
      <div className="max-w-7xl mx-auto flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-emerald-400 font-semibold mb-2">Team</p>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight">Workload board</h1>
        </div>
        <div className="flex gap-4 text-sm">
          <span className="flex items-center gap-1.5 text-amber-300" data-testid="workload-total-requests"><MessageSquareText className="h-4 w-4" />{totals.requests} open requests</span>
          <span className="flex items-center gap-1.5 text-rose-300" data-testid="workload-total-overdue"><AlertTriangle className="h-4 w-4" />{totals.overdue} overdue studies</span>
        </div>
      </div>
      <SatisfactionPanel data={sat} />
      <div className="max-w-7xl mx-auto flex gap-4 overflow-x-auto pb-4" data-testid="workload-board">
        {ordered.map((c) => <Column key={c.id} c={c} me={user?.id} sat={sat?.staff.find((x) => x.staff_id === c.id)} />)}
      </div>
      <p className="max-w-7xl mx-auto text-xs text-slate-500 mt-4">Set a study's assignee and delivery due date in the study editor. Cards with red borders are overdue; amber means open change requests.</p>
    </div>
  );
}
